import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { getEventsWithFlyerPaginated } from '~/api/gatherings';
import {
  IEventWithFlyer,
  IFlyerListParams,
  IPaginatedEventsWithFlyer,
} from '~/types/gathering';

const DEFAULT_LIMIT = 25;
const DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;

interface UseEventsWithFlyerPaginatedOptions {
  completed: boolean;
  enabled?: boolean;
}

export default function useEventsWithFlyerPaginated({
  completed,
  enabled = true,
}: UseEventsWithFlyerPaginatedOptions) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelSourceRef = useRef<{ token: any; cancel: (msg?: string) => void } | null>(null);

  // Track previous completed value to reset page synchronously (no wasted request)
  const prevCompletedRef = useRef(completed);
  if (prevCompletedRef.current !== completed) {
    prevCompletedRef.current = completed;
    if (page !== 1) {
      setPage(1);
    }
  }

  // Debounce the search input with minimum length gate
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const trimmed = value.trim();

      // Only trigger search if empty (clear) or meets minimum length
      if (trimmed.length === 0 || trimmed.length >= MIN_SEARCH_LENGTH) {
        setDebouncedSearch(trimmed);
        setPage(1);
      }
    }, DEBOUNCE_MS);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel('Component unmounted');
      }
    };
  }, []);

  const effectivePage = prevCompletedRef.current === completed ? page : 1;

  const params: IFlyerListParams = {
    page: effectivePage,
    limit: DEFAULT_LIMIT,
    completed,
    search: debouncedSearch,
    sortField: 'startTime',
    sortOrder: 'DESC',
  };

  // Stabilize queryKey reference so downstream useCallbacks don't churn
  const queryKey = useMemo(
    () => [
      'eventsWithFlyerPaginated',
      params.page,
      params.completed,
      params.search,
      params.sortField,
      params.sortOrder,
    ],
    [params.page, params.completed, params.search, params.sortField, params.sortOrder]
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<IPaginatedEventsWithFlyer>(
    queryKey,
    async () => {
      // Cancel any in-flight request before starting a new one
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel('New request started');
      }
      const source = axios.CancelToken.source();
      cancelSourceRef.current = source;

      try {
        const res = await getEventsWithFlyerPaginated(params, source.token);
        return res.data;
      } catch (err: any) {
        // Don't treat cancelled requests as errors
        if (axios.isCancel(err)) {
          return undefined as any;
        }
        throw err;
      }
    },
    {
      enabled,
      keepPreviousData: true,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    }
  );

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? {
    page: 1,
    limit: DEFAULT_LIMIT,
    total: 0,
    totalPages: 0,
    hasMore: false,
  };

  // ── Cache manipulation (surgical updates, no full refetch) ──

  /** Update a single event in the current page's cache */
  const updateItemInCache = useCallback(
    (eventUuid: string, updater: (item: IEventWithFlyer) => IEventWithFlyer) => {
      queryClient.setQueryData<IPaginatedEventsWithFlyer | undefined>(
        queryKey,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.uuid === eventUuid ? updater(item) : item
            ),
          };
        }
      );
    },
    [queryClient, queryKey]
  );

  /** Remove an event from the current page's cache (e.g. status crossed completed boundary) */
  const removeItemFromCache = useCallback(
    (eventUuid: string) => {
      queryClient.setQueryData<IPaginatedEventsWithFlyer | undefined>(
        queryKey,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((item) => item.uuid !== eventUuid),
            pagination: {
              ...old.pagination,
              total: Math.max(0, old.pagination.total - 1),
            },
          };
        }
      );
    },
    [queryClient, queryKey]
  );

  // ── Pagination controls ──

  const goToPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const nextPage = useCallback(() => {
    if (pagination.hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [pagination.hasMore]);

  const prevPage = useCallback(() => {
    setPage((prev) => Math.max(1, prev - 1));
  }, []);

  const clearSearch = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel('Search cleared');
    }
  }, []);

  return {
    items,
    pagination,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    completed,
    // Search
    search,
    handleSearchChange,
    clearSearch,
    // Pagination controls
    page: effectivePage,
    goToPage,
    nextPage,
    prevPage,
    // Cache manipulation
    updateItemInCache,
    removeItemFromCache,
  };
}
